import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { EmailRecipient } from "../../types/Client";

interface EmailRecipientsFieldProps {
  label: string;
  secondaryLabels: string;
  recipients: EmailRecipient[];
  onChange: (recipients: EmailRecipient[]) => void;
}

export const EmailRecipientsField: React.FC<EmailRecipientsFieldProps> = ({
  label,
  secondaryLabels,
  recipients,
  onChange,
}) => {
  const { t } = useTranslation();
  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");

  const handleAdd = () => {
    if (newEmail && newDisplayName) {
      onChange([
        ...recipients,
        { address: newEmail, displayName: newDisplayName },
      ]);
      setNewEmail("");
      setNewDisplayName("");
    }
  };

  const handleDelete = (index: number) => {
    const updated = recipients.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
        <TextField
          size="small"
          label={label}
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label={secondaryLabels}
          value={newDisplayName}
          onChange={(e) => setNewDisplayName(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={!newEmail || !newDisplayName}
          startIcon={<AddIcon sx={{ fontSize: "1.5rem" }} />}
          sx={{
            minWidth: "140px",
            height: "40px",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            borderRadius: 2,
            boxShadow: 2,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 4,
            },
            "&:active": {
              transform: "translateY(0px)",
            },
            "&:disabled": {
              opacity: 0.5,
            },
          }}
        >
          {t("ADD")}
        </Button>
      </Box>

      {recipients.length > 0 && (
        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: "auto" }}>
          <List dense>
            {recipients.map((recipient, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={recipient.displayName}
                  secondary={recipient.address}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, display: "block" }}
      >
        {t("MULTIPLE_EMAIL_RECIPIENTS_HINT")}
      </Typography>
    </Box>
  );
};
